import { useCallback, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { styled } from 'styled-components';

import { useAppContext } from '../contextApi/context';
import { getMovementDetails, editAnnulmentMovement } from '../services/StockMovementService';
import { ToggleOption, ModalButton } from '../styles/StyledComponents';
import Spinner from './shared/Spinner';

const EditMovementAnnulmentModal = ({ closeHandler, actionHandler, annulmentId, show }) => {
    // ----- Context
    const { loggedUser } = useAppContext();

    // ----- State
    let [movementDetails, setMovementDetails] = useState(null);
    let [originalAmounts, setOriginalAmounts] = useState(null);
    let [allGood, setAllGood] = useState(true);
    let [isComplete, setIsComplete] = useState(false);
    let [showStockError, setShowStockError] = useState(false);
    let [invoice, setInvoice] = useState(null);

    // ----- API Fetch Actions
    const fetchStockMovementDetails = useCallback(
        async (id) => {
            return await getMovementDetails(loggedUser.token, id);
        },
        [loggedUser.token]
    );

    // ----- Hooks
    useEffect(() => {
        fetchStockMovementDetails(annulmentId).then((annulmentData) => {
            let details = annulmentData.details.map((item) => {
                return {
                    id: item.id,
                    articleProviderId: item.articleProviderId,
                    providerId: item.providerId,
                    articleName: item.articleName,
                    articleId: item.articleId,
                    brand: item.brand,
                    amount: item.amount,
                    amountToAnnul: item.amount, // Start with current annulment amount
                    showError: false,
                    isService: item.isService,
                    serviceConcept: item.serviceConcept,
                };
            });

            // Getting receiptData based on annulmentData
            fetchStockMovementDetails(annulmentData.reversedMovement).then((receiptData) => {
                setInvoice(receiptData.invoice);

                let originals = [];

                annulmentData.details.forEach((returnDetail) => {
                    let found = receiptData.details.find((issueDetail) => {
                        if (issueDetail.articleName === returnDetail.articleName) {
                            return returnDetail;
                        }
                        return null;
                    });
                    originals.push(found?.amount);
                });

                setMovementDetails(details);
                setOriginalAmounts(originals);
            });
        });
    }, [fetchStockMovementDetails, annulmentId]);

    const handleApiErrors = (errorMessages) => {
        if (!errorMessages || !Array.isArray(errorMessages)) return;

        // Parse each error to extract articleId and brand
        const errorPairs = errorMessages.map((errorMsg) => {
            const articleMatch = errorMsg.match(/article\s+(\d+)/);
            const brandMatch = errorMsg.match(/brand\s+(\w+)/);

            return {
                articleId: articleMatch ? articleMatch[1] : null,
                brand: brandMatch ? brandMatch[1] : null,
            };
        });

        // Update movementDetails to show errors for matching items
        setMovementDetails((prevDetails) => {
            const updatedDetails = prevDetails.map((item) => {
                if (!item.isService) {
                    const hasError = errorPairs.some((error) => {
                        // Match by articleId and brand
                        return error.articleId === String(item.articleId) && error.brand === item.brand;
                    });

                    return {
                        ...item,
                        showError: hasError || item.showError,
                    };
                } else {
                    // Services don't get errors
                    return item;
                }
            });

            setShowStockError(true);
            setAllGood(!updatedDetails.some((item) => item.showError));

            return updatedDetails;
        });
    };

    // ----- Actions
    const handleClose = () => {
        setMovementDetails(null);
        closeHandler();
    };

    const handleAction = async () => {
        setShowStockError(false);

        let details = movementDetails.map((item) => {
            return {
                id: item.id,
                articleProviderId: !item.isService ? item.articleProviderId : null,
                amount: isComplete ? originalAmounts[movementDetails.indexOf(item)] : Number(item.amountToAnnul),
                brand: !isComplete && !item.isService ? item.brand : null,
                serviceConcept: item.isService ? item.serviceConcept : null,
                providerId: item.isService ? item.providerId : null,
                isService: item.isService,
                articleId: item.articleId,
            };
        });

        const payload = {
            details,
            annulmentType: isComplete ? 'COMPLETE' : 'PARTIAL',
        };

        const response = await editAnnulmentMovement(loggedUser.token, annulmentId, payload);
        if (response.status === 201) {
            actionHandler();
        } else {
            // Error from API
            const { data } = response;

            if (data && Array.isArray(data) && data.length > 0) {
                handleApiErrors(data);
            }
        }
    };

    const handleInputChange = (detailIndex, e) => {
        let amountToAnnul = e.target.value;

        let newMovementDetails = movementDetails.map((item, index) => {
            if (index === detailIndex) {
                item.amountToAnnul = amountToAnnul;

                if (amountToAnnul === '' || Number(amountToAnnul) > originalAmounts[index]) {
                    item.showError = true;
                } else {
                    item.showError = false;
                }
            }
            return item;
        });

        setMovementDetails(newMovementDetails);
        setAllGood(!newMovementDetails.some((e) => e.showError));
    };

    const handleKeyDown = (e) => {
        const acceptedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'ArrowLeft', 'ArrowRight', 'Backspace', 'Delete'];

        if (!acceptedKeys.includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleAnnulmentTypeChange = (e) => {
        const isCompleteType = e.target.value === 'true';
        setIsComplete(isCompleteType);

        // Re-validate all inputs when switching back to partial mode
        if (movementDetails && originalAmounts) {
            const updatedDetails = movementDetails.map((item, index) => {
                // Re-check for errors based on current values
                const hasError = !isCompleteType && (item.amountToAnnul === '' || Number(item.amountToAnnul) > originalAmounts[index]);

                return {
                    ...item,
                    showError: hasError,
                };
            });

            setMovementDetails(updatedDetails);
            setAllGood(!updatedDetails.some((e) => e.showError));
        }
    };

    // ----- Render
    return (
        <Modal show={show} size='lg' backdrop='static' centered onHide={handleClose}>
            {movementDetails && originalAmounts ? (
                <>
                    <Modal.Header className='fw-bold' closeButton>
                        Editar anulación - Factura #{invoice}
                    </Modal.Header>
                    <Modal.Body>
                        <div className='d-flex flex-column'>
                            <ToggleOption className='d-flex justify-content-center mb-3'>
                                <input
                                    type='radio'
                                    className='btn-check'
                                    name='annulmentType'
                                    id='partialType'
                                    value='false'
                                    onChange={handleAnnulmentTypeChange}
                                    checked={!isComplete}
                                />
                                <label className='btn btn-outline-success first-option' htmlFor='partialType'>
                                    Parcial
                                </label>

                                <input
                                    type='radio'
                                    className='btn-check'
                                    name='annulmentType'
                                    id='completeType'
                                    value='true'
                                    onChange={handleAnnulmentTypeChange}
                                    checked={isComplete}
                                />
                                <label className='btn btn-outline-success second-option' htmlFor='completeType'>
                                    Completa
                                </label>
                            </ToggleOption>

                            <label className='fw-bold'>Detalles</label>
                            <p className='mb-1 ms-2'>
                                {isComplete
                                    ? 'Nota: La compra completa será anulada (Esta acción es irreversible).'
                                    : 'Nota: Las cantidades a anular no pueden ser mayores a la cantidad original de la compra.'}
                            </p>

                            {showStockError && <Error className='ms-2 '>* Inventario insuficiente para los artículos marcados con X.</Error>}

                            <table className='table table-bordered my-2'>
                                <thead>
                                    <tr>
                                        <th scope='col'>Concepto</th>
                                        <th scope='col'>Cantidad original</th>
                                        <th scope='col'>Anulación</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {movementDetails.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.isService ? item.serviceConcept : `${item.articleName} (${item.brand})`}</td>
                                            <td>{originalAmounts[index]}</td>
                                            <ReturnColumn>
                                                <input
                                                    type='text'
                                                    value={isComplete ? originalAmounts[index] : item.amountToAnnul}
                                                    maxLength={10}
                                                    disabled={isComplete}
                                                    onKeyDown={!isComplete ? handleKeyDown : undefined}
                                                    onChange={!isComplete ? (e) => handleInputChange(index, e) : undefined}
                                                />
                                                <span className='ms-2'>{item.showError && 'x'}</span>
                                            </ReturnColumn>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <ModalButton className='cancel' onClick={handleClose}>
                            Cancelar
                        </ModalButton>
                        <ModalButton className='action' onClick={handleAction} disabled={!allGood}>
                            Guardar
                        </ModalButton>
                    </Modal.Footer>
                </>
            ) : (
                <Spinner />
            )}
        </Modal>
    );
};

const Error = styled.span`
    color: #d04437;
    font-size: 14px;
`;

const ReturnColumn = styled.td`
    align-items: center;
    display: flex;
    flex-direction: row;

    > input {
        width: 70px;
    }

    > span {
        color: #d04437;
        font-size: 16px;
        font-weight: bold;
        margin-left: 0.5rem;
    }
`;

export default EditMovementAnnulmentModal;
